import { apiClient } from "./api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";

const SESSION_KEY = "goodglows_sessionid";

/** True when the visitor already has a session id (localStorage or a readable cookie). */
function hasSession(): boolean {
  if (localStorage.getItem(SESSION_KEY)) return true;
  return document.cookie
    .split("; ")
    .some((c) => c.startsWith(`${SESSION_KEY}=`));
}

/**
 * Extract the invite code from a `/code/<code>` landing URL (with or without
 * the `/secretmissions` basepath). Returns the raw code string or undefined.
 */
function inviteCodeFromUrl(): string | undefined {
  const m = window.location.pathname.match(
    /^\/(?:secretmissions\/)?code\/([^/]+)/,
  );
  return m?.[1];
}

/**
 * On a brand-new visit — no `goodglows_sessionid` anywhere — mint a temporary
 * member + session so session-bound calls (codename, journey, key-qr) resolve
 * to a real member. The phone is filled in later. Idempotent: if a session
 * already exists this returns immediately without hitting the backend.
 *
 * Best-effort — a failure must never block the app from rendering; the visitor
 * just proceeds anonymously until a later call establishes a session.
 */
export async function ensureSession(): Promise<void> {
  if (hasSession()) return;
  try {
    // If this is an invite landing (/code/<code>), pass the KEY code so the
    // backend can attribute the new member to the KEY owner (RecruitedBy).
    const referrerCode = inviteCodeFromUrl();
    const body: Record<string, string> = {};
    if (referrerCode) body.referrerCode = referrerCode;

    // the first render waits on this: a stalled server must not hold a blank
    // page, so it gives up and they go on anonymously
    const { data } = await apiClient.post(API_ENDPOINTS.session.ensure, body, {
      timeout: 4000,
    });
    const sid = data?.result?.sessionId as string | undefined;
    // Persist alongside the (httponly) cookie the backend set, so api-client's
    // localStorage path also carries it (mobile-Safari resilience).
    if (sid) localStorage.setItem(SESSION_KEY, sid);
  } catch {
    /* offline / mock without a handler — proceed anonymously */
  }
}
